// src/views/UserViews/insights/components/FeatureComplianceRow.tsx
//
// One compact row inside the merged "Safety feature compliance" card: feature label
// left-aligned, its percentage right-aligned, on a single line. A sibling to MetricCard
// rather than a third `variant` on it - MetricCard's content is always a vertical
// label/value/caption column and has no branch for a one-line two-sided row (the same
// reasoning UtilizationProgressCard's header comment records for its own existence).
//
// Rule 1 is reproduced, not re-derived: `null` renders an em dash plus a caption, never `0`,
// `0.0%`, or "-%", while a genuine zero against a real denominator IS valid data. Testids
// follow MetricCard's `${testId}-value`/`-empty`/`-loading`/`-error` convention so existing
// per-feature assertions transfer unchanged.

import { Box, Skeleton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';

type FeatureComplianceRowProps = {
  testId: string;
  label: string;
  /** Raw ratio in [0, 1]; `null` means "not enough data" / "not applicable" (Rule 1). */
  value: number | null;
  tooltip?: string;
  /** Overrides the default "Not enough data" wording - e.g. head impact's Pro-only case. */
  nullCaption?: string;
  /** Overrides `VALUE_SX.color` for the populated percentage only - same name/shape as
   * MetricCard's own `valueColor` prop, and the same guarantee: the null em-dash branch
   * below always keeps `VALUE_SX`'s own `'primary.main'` regardless of what's passed here,
   * so a caller's threshold color (see `valueColorForScore` in FeatureComplianceSection.tsx)
   * can never accidentally tint the "not enough data" state (Rule 1). Falls back to
   * `VALUE_SX`'s default `'primary.main'` when omitted. */
  valueColor?: string;
  loading?: boolean;
  error?: Error | null;
};

const PERCENT_MULTIPLIER = 100;
const PERCENT_DECIMAL_PLACES = 1;
const EM_DASH = '\u2014';
// 64px (was 56px): scaled ~1.125x alongside VALUE_SX's fontSize bump (16px -> 18px) so the
// loading skeleton still spans roughly the same text it stands in for (e.g. "100.0%").
const SKELETON_WIDTH = 64;
// Label size no longer reuses MetricCard's own label tokens (that comment was stale even
// before this change - MetricCard's labels moved to subtitle1/700 during the frozen KPI
// hover polish). Bumped independently here, moderately, so the label stays legible next to
// the larger VALUE_SX percentage below without matching MetricCard's own scale 1:1.
const LABEL_SX = { fontSize: '0.9375rem', fontWeight: 600 }; // 15px (was 13px)
// fontSize is now explicit (previously unset, so this Typography inherited the default
// "body1" variant's 1rem/16px) rather than relying on an implicit default - the percentage
// is the focal point of each row and reads clearly larger than the 15px label at 18px. The
// em-dash empty state (below) intentionally shares this same object so populated and
// null rows keep one visual baseline (Rule 1).
const VALUE_SX = { fontWeight: 700, color: 'primary.main', fontSize: '1.125rem' }; // 18px (was 16px)

export default function FeatureComplianceRow({
  testId,
  label,
  value,
  tooltip,
  nullCaption,
  valueColor,
  loading = false,
  error = null,
}: FeatureComplianceRowProps) {
  const { t } = useTranslation();
  const isEmpty = value === null;
  // Mirrors MetricCard's own `populatedValueSx` derivation: only the populated branch below
  // ever sees `valueColor` - the empty em-dash branch always renders plain `VALUE_SX`,
  // enforcing Rule 1 in code rather than trusting every caller to pass `undefined` for null.
  const populatedValueSx = valueColor ? { ...VALUE_SX, color: valueColor } : VALUE_SX;

  return (
    <Box
      data-testid={testId}
      sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={LABEL_SX}>
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} placement="top" arrow>
            <InfoOutlinedIcon aria-label={tooltip} sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          </Tooltip>
        )}
      </Box>

      {loading && <Skeleton variant="text" width={SKELETON_WIDTH} data-testid={`${testId}-loading`} />}

      {!loading && error && (
        <Typography variant="body2" color="error" data-testid={`${testId}-error`}>
          {t(StringKeys.INSIGHTS_SECTION_LOAD_ERROR)}
        </Typography>
      )}

      {!loading && !error && isEmpty && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {nullCaption ?? t(StringKeys.INSIGHTS_METRIC_NOT_ENOUGH_DATA)}
          </Typography>
          <Typography component="span" data-testid={`${testId}-empty`} sx={VALUE_SX}>
            {EM_DASH}
          </Typography>
        </Box>
      )}

      {!loading && !error && !isEmpty && (
        <Typography component="span" data-testid={`${testId}-value`} sx={populatedValueSx}>
          {`${(value * PERCENT_MULTIPLIER).toFixed(PERCENT_DECIMAL_PLACES)}%`}
        </Typography>
      )}
    </Box>
  );
}
